import { BaseDriver } from './device'
import { ResDeviceState } from '../../libs/types/response'
import { DeviceProtocol } from '../../hid/device/device_model'
import { OutputMode, type VK, VirtualMer68SESelfDev } from '../../hid/define/define'
import { defaultVK } from '../../hid/define/define_keys'
import { lightMatrix } from '../../hid/define/define_light_matrix'
import { KK } from '../../hid/define/define'
import { deviceManager } from '../../hid/device/device_manager'
import { bytesPadding64, makeData64WithCRC, makeDataWithCRC, printHex } from '@libs/utils/tools'
import { DeviceConnectMode, SpecificItem } from '@libs/types/enum'
import { musicData } from '@libs/music/music'
import { fe87V2Data } from './data_conversion/fe87v2'
import { Fe87V2Magnet } from './magent/fe87v2'

enum Fe87V2CMDType {
  SetLeds = 0x18,
  SetCustomColor = 0x2a,
}
export class fe87v2Driver extends BaseDriver {
  declare magnet: Fe87V2Magnet | undefined
  musicIns: musicData

  constructor(public DeviceHID: DeviceProtocol) {
    super(DeviceHID)
    this.magnet = new Fe87V2Magnet(this.DeviceHID, this)
    this.musicIns = new musicData(this.onMusicData)
  }
  async pair24g() {
    return 0
  }
  updateFirmware(): Promise<boolean> {
    throw new Error('Method not implemented.')
  }

  makeCMDSelectArg(keys: KK[], select: number, secound?: number): Map<KK, number[]> {
    const res = new Map<KK, number[]>()
    keys.forEach((k) => {
      res.set(k, [select, secound == undefined ? 0xff : secound, 0xff])
    })
    return res
  }

  makeSimple(data: number[]): Map<KK, number[]> {
    const res = new Map<KK, number[]>([[data[0] as KK, data.slice(1)]])
    return res
  }

  async cmdKey(cmd: number, sets: Map<KK, number[]>, write = false): Promise<Map<KK, [number, number]>> {
    if (sets.size == 0) return new Map<KK, [number, number]>()
    let index = 0
    let prefixed = [0x5c, 0, cmd, 0, write ? 1 : 0]
    const data: number[][] = [[...prefixed]]
    sets.forEach((v, k) => {
      if (data[index].length >= 64 - v.length) {
        index++
        data[index] = [...prefixed]
      }
      data[index].push(k, ...v)
    })
    const resp = new Map<KK, [number, number]>()
    for (let i = 0; i < data.length; i++) {
      data[i][1] = data[i].length - 4
      data[i][3] = makeDataWithCRC(data[i])
      const toSend = bytesPadding64(data[i], 64, [0xff])
      const r = await this.DeviceHID.send(toSend)
      if (r[4] != 0x00) {
        printHex(r, 'receive:')
        printHex(toSend, 'send:')
        throw new Error('getKeysTravel error')
      }
      if (!write) {
        for (let j = 5; j < r.length; j += 4) {
          if (j > r.length - 3) {
            break
          }
          if (r[j] == 0xff) continue
          if (cmd == 0x2c) {
            resp.set(r[j] as KK, [r[j + 1], 0])
            if (r[j + 2] != 0 && r[j + 2] != 0xff) {
              resp.set(r[j + 2] as KK, [r[j + 3], 0])
            }
          } else {
            resp.set(r[j] as KK, [r[j + 2], r[j + 3]])
          }
        }
      }
    }
    return resp
  }

  async syncKeys() {
    await this.musicIns.musicCapturePause()
    const keys = this.save.Keys

    const change = new Map<KK, number[]>()
    const changeFn = new Map<KK, number[]>()

    const changeOfReset = new Map<KK, number[]>() //重置普通层
    const changeOfResetFn = new Map<KK, number[]>() //重置Fn层
    const changeOfResetSenior = new Map<KK, number[]>() //重置高级改键层

    //同步普通改键
    keys.forEach((item) => {
      if (item.Output.Mode == OutputMode.Key && item.Output.VK == defaultVK) {
        changeOfResetSenior.set(item.Input.KK, [8, 0, 0])
        changeOfReset.set(item.Input.KK, [0, item.Input.KK, 0])
        this.setFnKeyData(changeOfResetFn, item.Input.KK, false)
      } else if (item.Output.Mode == OutputMode.Key) {
        const keyData = fe87V2Data.encodeKey(item)
        if (item.Input.WithFN) {
          changeFn.set(item.Input.KK, [keyData[1], keyData[2], keyData[3]])
        } else {
          changeOfResetSenior.set(item.Input.KK, [8, 0, 0])
          change.set(item.Input.KK, [keyData[1], keyData[2], keyData[3]])
        }
      }
    })

    if (changeOfResetSenior.size > 0) await this.cmdKey(0x23, changeOfResetSenior, true)
    if (changeOfReset.size > 0) await this.cmdKey(0x23, changeOfReset, true)
    if (changeOfResetFn.size > 0) await this.cmdKey(0x23, changeOfResetFn, true)
    if (change.size > 0) await this.cmdKey(0x23, change, true)
    if (changeFn.size > 0) await this.cmdKey(0x23, changeFn, true)

    this.clearResetKeys()
    await this.magnet?.clearKmeBind()
    await this.musicIns.musicCaptureContinue()
  }

  setFnKeyData(changeOfResetFn: Map<KK, number[]>, key: KK, isReset: boolean) {
    let setValue = isReset ? 0x01 : key
    let fnKey = fe87V2Data.getFnDisableKeysData(deviceManager.deviceID).get(key)
    if (fnKey != undefined) {
      changeOfResetFn.set(key, [1, fnKey[0], fnKey[1]])
    } else {
      changeOfResetFn.set(key, [1, setValue, 0])
    }
  }

  async getVersion() {
    const data = makeData64WithCRC([0x5c, 0x02, 0x01, 0x00, 0xff, 0xff])
    const res = await this.DeviceHID.send(data)
    if (res[4] != 0x00) {
      throw new Error('getVersion error')
    }
    const version = res.slice(30, 44)
    let str = version.map((v) => String.fromCharCode(v)).join('')
    const fd = str.match(/V(\d+\.?)+/g)
    if (fd) {
      str = fd[0].replace('V', '')
    }
    if (str.match(/^(\d\.{0,1})+/g) == null) {
      throw new Error('getVersion error')
    }
    return str
  }

  async getDeviceState(): Promise<ResDeviceState> {
    const res: ResDeviceState = {
      ConnectType: DeviceConnectMode.Normal,
      IsConnect: true,
      IsSleep: false,
    }
    return res
  }

  private async getLockWin(): Promise<boolean> {
    const data = Array(64).fill(0)
    data[0] = 0x5c
    data[1] = 0x03
    data[2] = 0x00
    data[3] = 0x93
    data[4] = 0x20
    data[5] = 0xff
    data[6] = 0xff
    const res = await this.DeviceHID.send(data)
    if (res[4] != 0x00) {
      throw new Error('getLockWin error' + res[4])
    }
    return Boolean(res[6])
  }

  async syncLeds() {
    await this.musicIns.musicCapturePause()
    const lightMode = this.save.LightMode
    const decorates = this.save.Decorates

    let sideMode = lightMode.SideMode
    if (decorates && decorates.length > 0) {
      sideMode = decorates[0].LightMode.Mode
    }

    await this.changeLightColor(lightMode.Mode, lightMode.Speed, lightMode.Light, lightMode.Color, lightMode.Direction, sideMode, lightMode.FullColor)
    await this.musicIns.musicCaptureContinue()
  }

  async syncDecorationLed() {
    if (deviceManager.deviceID == VirtualMer68SESelfDev) {
      return
    }
    const lightMode = this.save.LightMode
    const decorates = this.save.Decorates

    if (decorates == undefined || decorates.length == 0) {
      throw new Error('Decorates is undefined')
    }
    await this.changeLightColor(lightMode.Mode, decorates[0].LightMode.Speed, decorates[0].LightMode.Light, decorates[0].LightMode.Color, decorates[0].LightMode.Direction, decorates[0].LightMode.Mode, decorates[0].LightMode.FullColor)
  }

  private async changeLightColor(mode: number, speed: number, light: number, color: number, direction: number, sideMode: number, fullColor: boolean = false) {
    const { r, g, b } = this.numberToRgb(color)
    const data = Array(64).fill(0)
    data[0] = 0x5c
    data[1] = 0x2c
    data[2] = Fe87V2CMDType.SetLeds
    data[3] = 0xd4
    data[4] = 0x01
    console.log('changeLightColor', fullColor)
    // 非全彩模式，需要设置颜色
    if (!fullColor) {
      console.log('set color', { r, g, b })
      data[9] = b
      data[10] = g
      data[11] = r
    }
    data[42] = light
    data[43] = mode
    data[44] = speed
    data[45] = sideMode // 装饰灯
    data[46] = 0xff
    data[47] = 0xff

    const readData = await this.readbitmap()
    data[41] = readData[41] | 1 //打开灯效
    if (direction == 0) {
      data[41] = data[41] & 0b11111101
    } else {
      data[41] = data[41] | 0b0010
    }
    printHex(data, 'set led data:')
    const res = await this.DeviceHID.send(data)
    if (res[4] != 0x00) {
      throw new Error('syncLeds error' + res[4])
    }
  }

  // private async turnOnColorCustom() {
  //   const lightMode = this.save.LightMode
  //   const data = Array(64).fill(0)
  //   data[0] = 0x5c
  //   data[1] = 0x2c
  //   data[2] = Fe87V2CMDType.SetLeds
  //   data[3] = 0xd4
  //   data[4] = 0x01
  //   data[42] = lightMode.Light
  //   data[43] = 0x0e
  //   data[44] = 0x03

  //   data[45] = 0x00 // SLEEP[1B] 休眠时间，默认 0x00 为永久
  //   data[46] = 0xff
  //   data[47] = 0xff

  //   const readData = await this.readbitmap()
  //   data[41] = readData[41] | 1 //打开灯效
  //   if (lightMode.Direction == 0) {
  //     data[41] = data[41] & 0b11111101
  //   } else {
  //     data[41] = data[41] | 0b0010
  //   }
  //   const res = await this.DeviceHID.send(data)
  //   if (res[4] != 0x00) {
  //     throw new Error('syncLeds error' + res[4])
  //   }
  // }

  // private async turnOnColorCustom() {
  //   const lightMode = this.save.LightMode
  //   const data = Array(64).fill(0)
  //   data[0] = 0x5c
  //   data[1] = 0x2c
  //   data[2] = Fe87V2CMDType.SetLeds
  //   data[3] = 0xd4
  //   data[4] = 0x01
  //   data[42] = lightMode.Light
  //   data[43] = 0x0e
  //   data[44] = 0x03

  //   data[45] = 0x00 // SLEEP[1B] 休眠时间，默认 0x00 为永久
  //   data[46] = 0xff
  //   data[47] = 0xff

  //   const readData = await this.readbitmap()
  //   data[41] = readData[41] | 1 //打开灯效
  //   if (lightMode.Direction == 0) {
  //     data[41] = data[41] & 0b11111101
  //   } else {
  //     data[41] = data[41] | 0b0010
  //   }
  //   const res = await this.DeviceHID.send(data)
  //   if (res[4] != 0x00) {
  //     throw new Error('syncLeds error' + res[4])
  //   }
  // }

  private numberToRgb(num: number): { r: number; g: number; b: number } {
    const r = (num >> 16) & 0xff
    const g = (num >> 8) & 0xff
    const b = num & 0xff
    return { r, g, b }
  }

  async setCustomColorOneKey(key: KK, color: number) {
    const { r, g, b } = this.numberToRgb(color)
    const data = new Map<KK, number[]>([[key, [r, g, b]]])
    await this.cmdKey(0x2a, data, true)
  }

  async syncCustomColor() {
    //   return
    //   await this.musicIns.musicCapturePause()
    //   //TODO 如果已经是自定义颜色，不需要再次发送
    //   if (this.save.LightLastIsCustom) {
    //     await this.turnOnColorCustom()
    //     await delay(500) //不加延迟, 可能会导致ESC键自定义灯效失败
    //   }
    //   const data = new Map<KK, number[]>()
    //   const keys = allKeysForDevice(deviceManager.deviceID)
    //   keys.forEach(async (key) => {
    //     data.set(key, [0, 0, 0])
    //   })
    //   this.save.ColorsCustom.forEach((value, key) => {
    //     const { r, g, b } = this.numberToRgb(value)
    //     data.set(key, [r, g, b])
    //   })
    //   console.log(data)
    //   await this.cmdKey(0x2a, data, true)
    //   this.saveCustomColorBoard()
    //   await this.musicIns.musicCaptureContinue()
  }

  // private async saveCustomColorBoard() {
  //   await this.cmdKey(0x2a, new Map<KK, number[]>([[0xfe as KK, [0xfe, 0xfe, 0xfe]]]), true)
  // }

  async syncDynamicColor(): Promise<boolean> {
    return true
    //   const colorsDynamic = this.save.ColorsDynamic
    //   if (this.changeProfileFlag && colorsDynamic.Run == false) {
    //     return true
    //   }
    //   let state = MusicCaptureStatus.Stop
    //   if (colorsDynamic.Run) {
    //     if (!this.save.LightLastIsCustom) {
    //       await this.turnOnColorCustom()
    //     }
    //     state = MusicCaptureStatus.Start
    //   }
    //   return this.musicIns.dynamicColorState(state)
  }

  private onMusicData = async (data: number[]) => {
    if (this.save.ColorsDynamic.Run == false) {
      return
    }
    if (data.length > 0) {
      this.sendEffectData(data)
    }
  }

  async syncImageLightFrame(data: number[]): Promise<boolean> {
    if (data.length === 0) return false
    await this.sendEffectData(data)
    return true
  }

  private async sendEffectData(data: number[]) {
    let keyMap = lightMatrix(deviceManager.deviceID)
    let sendKeys = new Map<KK, number[]>()
    for (let i = 0; i < keyMap.size; i++) {
      let k = keyMap.get(i)
      if (k == undefined || k == 0) {
        continue
      }
      sendKeys.set(k as KK, [data[i * 3], data[i * 3 + 1], data[i * 3 + 2]])
    }
    await this.cmdKey(0x2a, sendKeys, true)
  }

  async syncConfig() {
    this.setAllSpecificItem()
  }

  private async setAllSpecificItem() {
    const items = this.save.Items
    for (const [key, item] of items) {
      if (key == SpecificItem.Rate || key == SpecificItem.LockWin || key == SpecificItem.RunMode) {
        continue
      }
      await this.setOrReadSpecificItem(key, true, item)
    }
  }

  async setOrReadSpecificItem(item: string, isWrite: boolean, value: any) {
    const item2 = item as SpecificItem

    if (item2 == SpecificItem.LockWin) {
      if (isWrite) throw new Error('LockWin is read only')
      return await this.getLockWin()
    }

    if (item2 == SpecificItem.Rate) {
      return await this.setOrReadRate(isWrite, value)
    }

    if (item2 == SpecificItem.KeyboardDebounce) {
      return await this.setOrReadKeyboardDebounce(isWrite, value)
    }

    if (item2 == SpecificItem.IMCStatus) {
      return await this.setOrReadIMCStatus(isWrite, value)
    }

    if (item2 == SpecificItem.RunMode && !isWrite) {
      return await this.readRunMode()
    }

    if (item2 == SpecificItem.SuperResponsiveMode) {
      if (typeof value != 'boolean') throw new Error('value must be boolean')
      return await this.setSuperResponsiveMode(value, isWrite)
    }

    if (item2 == SpecificItem.FastScanningTime) {
      return await this.setOrReadFastScanningTime(isWrite, value)
    }

    if (item2 == SpecificItem.RTScanningTime) {
      return await this.setOrReadRTScanningTime(isWrite, value)
    }

    return null
  }

  async setOrReadFastScanningTime(isWrite: boolean, value: number) {
    const scanningTime = await this.getScanningTime()
    if (!isWrite) {
      return (scanningTime[6] << 8) | scanningTime[5]
    }

    if (value < 170 || value > 500) {
      throw new Error('value must be between 170 and 500')
    }
    scanningTime[0] = 0x5c
    scanningTime[1] = 0x06
    scanningTime[2] = 0x52
    scanningTime[4] = 1
    scanningTime[5] = value & 0xff
    scanningTime[6] = (value >> 8) & 0xff
    scanningTime[3] = (0x35 + scanningTime[0] + scanningTime[1] + scanningTime[2] + 0xff) & 0xff
    const res = await this.DeviceHID.send(scanningTime)
    if (res[4] != 0x00) {
      throw new Error('setFastScanningTime failed')
    }
    this.scanningTime = []
    return true
  }

  async setOrReadRTScanningTime(isWrite: boolean, value: number) {
    const scanningTime = await this.getScanningTime()

    if (!isWrite) {
      return (scanningTime[8] << 8) | scanningTime[7]
    }
    if (value < 380 || value > 1000) {
      throw new Error('value must be between 380 and 1000')
    }
    scanningTime[0] = 0x5c
    scanningTime[1] = 0x06
    scanningTime[2] = 0x52
    scanningTime[4] = 1
    scanningTime[7] = value & 0xff
    scanningTime[8] = (value >> 8) & 0xff
    scanningTime[3] = (0x35 + scanningTime[0] + scanningTime[1] + scanningTime[2] + 0xff) & 0xff
    const res = await this.DeviceHID.send(scanningTime)
    if (res[4] != 0x00) {
      throw new Error('setRTScanningTime failed')
    }
    this.scanningTime = []
    return true
  }

  private scanningTime: number[] = []
  async getScanningTime() {
    if (this.scanningTime.length > 0) {
      return this.scanningTime
    }
    const data = Array(64).fill(0)
    data[0] = 0x5c
    data[1] = 0x06
    data[2] = 0x52
    data[4] = 0x00
    data[9] = 0xff
    data[3] = (0x35 + data[0] + data[1] + data[2] + 0xff) & 0xff
    const res = await this.DeviceHID.send(data)
    if (res[4] != 0x00) {
      throw new Error('getScanningTime failed')
    }
    this.scanningTime = res
    setTimeout(() => {
      this.scanningTime = []
    }, 1000)
    return this.scanningTime
  }

  private async setSuperResponsiveMode(value: boolean, isWrite: boolean) {
    let readData = Array(64).fill(0)
    readData[0] = 0x5c
    readData[1] = 0x3
    readData[2] = 0x50
    readData[4] = isWrite ? 1 : 0
    if (isWrite) {
      readData[5] = value ? 1 : 0
    }
    readData[6] = 0xff
    readData[3] = (0x35 + readData[0] + readData[1] + readData[2] + 0xff) & 0xff

    const resData = await this.DeviceHID.send(readData)
    if (resData[4] != 0x00) {
      throw new Error('handleSuperResponsiveMode failed')
    }

    if (resData[5] == 1 || isWrite) {
      return true
    }
    return false
  }

  async readRunMode() {
    const data = makeData64WithCRC([0x5c, 0x02, 0x01, 0x00, 0xff, 0xff])
    const res = await this.DeviceHID.send(data)
    if (res[4] != 0x00) {
      throw new Error('readRunMode error')
    }
    const version = res.slice(30, 44)
    let str = version.map((v) => String.fromCharCode(v)).join('')
    const strArr = str.split(' ')
    return strArr[0]
  }

  async setOrReadIMCStatus(isWrite: boolean, value: number) {
    const data = Array(64).fill(0)
    data[0] = 0x5c
    data[1] = 0x03
    data[2] = 0x53
    data[4] = 0x00
    data[6] = 0xff
    if (isWrite) {
      data[4] = 0x01
      data[5] = value
    }
    data[3] = (0x35 + data[0] + data[1] + data[2] + 0xff) & 0xff
    const res = await this.DeviceHID.send(data)
    if (res[4] != 0x00) {
      throw new Error('setOrReadIMCStatus failed')
    }
    return res[5] == 1
  }

  async setOrReadKeyboardDebounce(isWrite: boolean, value: number) {
    const data = Array(64).fill(0)
    data[0] = 0x5c
    data[1] = 0x03
    data[2] = 0x51
    data[4] = 0x00
    if (isWrite) {
      data[4] = 0x01
      data[5] = value
      data[6] = 0xff
    } else {
      data[5] = 0xff
      data[6] = 0xff
    }

    data[3] = (0x35 + data[0] + data[1] + data[2] + 0xff) & 0xff
    const res = await this.DeviceHID.send(data)
    if (res[4] != 0x00) {
      throw new Error('setRate failed')
    }

    const index = res[5]
    return index
  }

  async setOrReadRate(isWrite: boolean, value: number) {
    const RateList = [8000, 4000, 2000, 1000]

    const data = Array(64).fill(0)
    data[0] = 0x5c
    data[1] = 0x03
    data[2] = 0x00
    data[4] = 0x50
    if (isWrite) {
      const valueIndex = RateList.findIndex((v) => v == value)
      if (valueIndex == -1) {
        throw new Error('Rate not found:' + value)
      }
      data[1] = 0x04
      data[5] = valueIndex
      data[6] = 0xff
      data[7] = 0xff
    } else {
      data[5] = 0xff
      data[6] = 0xff
    }

    data[3] = (0x35 + data[0] + data[1] + data[2] + 0xff) & 0xff
    const res = await this.DeviceHID.send(data)
    if (res[4] != 0x00) {
      throw new Error('setRate failed')
    }

    const index = res[6]
    if (RateList[index] == undefined) {
      throw new Error('Rate not found:' + index)
    }
    return RateList[index]
  }

  private async readbitmap(): Promise<number[]> {
    const data = Array(64).fill(0)
    data[0] = 0x5c
    data[1] = 0x2c
    data[2] = 0x18
    data[3] = (0x35 + data[0] + data[1] + data[2] + 0x00) & 0xff
    const res = await this.DeviceHID.send(data)
    if (res[4] != 0x00) {
      throw new Error('readbitmap failed')
    }
    return res
  }

  async syncMacros() {
    await this.musicIns.musicCapturePause()
    const keys = this.save.Keys
    const macros = this.save.Macros

    let onceGroupLen = 14
    let keyDataLen = 4

    for (const item of keys) {
      if (item.Output.Mode == OutputMode.Macro) {
        const macro = macros.get(item.Output.DataIndex)
        if (macro && macro.length > 0) {
          const params: number[] = []
          macro.forEach((r) => {
            params.push(...fe87V2Data.genarateMacroData(r, keyDataLen))
          })

          const groupLen = Math.ceil(params.length / keyDataLen / onceGroupLen)
          for (let index = 0; index < groupLen; index++) {
            const len = index == groupLen - 1 ? (params.length / keyDataLen) % onceGroupLen : onceGroupLen
            const data = Array(64).fill(0)
            data[0] = 0x5c
            data[1] = len * keyDataLen + 4
            data[2] = 0x20
            data[4] = 0x01

            data[5] = index * onceGroupLen
            data[6] = 0x00

            //data[7] = len == 0 ? onceGroupLen : len;
            data[7] = macro.length

            for (let k = 8; k < keyDataLen * onceGroupLen + 8; k++) {
              if (params[index * keyDataLen * onceGroupLen + k - 8]) {
                data[k] = params[index * keyDataLen * onceGroupLen + k - 8]
              }
            }

            data[3] = (0x35 + data[0] + data[1] + data[2] + data[len * keyDataLen + 7]) & 0xff
            const resData = await this.DeviceHID.send(data)
            if (resData[4] != 0x00) {
              throw new Error('sync macro error')
            }
          }

          const macroKeyData = fe87V2Data.macroData(item)

          const sets: Map<KK, number[]> = new Map()
          sets.set(item.Input.KK, macroKeyData)
          await this.cmdKey(0x23, sets, true)
        }
      }
    }
    await this.musicIns.musicCaptureContinue()
  }

  async reset() {
    const resetData = [0x5c, 0x03, 0x00, 0x93, 0x11, 0xff, 0xff]
    const res = await this.DeviceHID.send(resetData)
    if (res[4] != 0x00) {
      throw new Error('reset error')
    }
  }

}
